import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/Input/Input';
import { Button } from 'src/components/ui/button';
import { useAuth } from 'src/provider/AuthProvider';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { emailScheme, stringScheme } from 'src/utils/validation-schemes';
import { useLoader } from 'src/provider/LoaderProvider';
import { useTokenMutation } from 'src/services/authSsoService';
import { PATH_HOME } from 'src/constants/spa-routes';
import { useGetAccountsQuery } from 'src/services/accountsService';

export const LoginForm = ({ onSubmit = () => {} }) => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const loader = useLoader();

  const [signIn, { data: signInData, isLoading: isLoadingSignIn, isSuccess: isSuccessSignIn }] =
    useTokenMutation();
  const { data: account } = useGetAccountsQuery(undefined, { skip: !isSuccessSignIn });

  useEffect(() => {
    if (isLoadingSignIn) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingSignIn]);

  useEffect(() => {
    if (isSuccessSignIn && account) {
      localStorage.setItem('API_VERSION', account.version);
      navigate(PATH_HOME.ROOT);
    }
  }, [isSuccessSignIn, account]);

  useEffect(() => {
    if (signInData && isSuccessSignIn) {
      setUser(signInData as Record<string, unknown>);
    }
  }, [signInData]);

  const submit = (data) => {
    const { email, password } = data;

    onSubmit();
    signIn({ email, password });
  };

  const { handleSubmit, trigger, control, clearErrors } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(
      object({
        email: emailScheme({ required: true }).label('Email'),
        password: stringScheme({
          required: true,
          min: 3,
          minMessage: 'Min. 3 characters',
        }).label('Password'),
      }),
    ),
  });

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit(submit)}>
        <div className={'input-container flex flex-col gap-[3px]'}>
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'email'}
                label={'Email'}
                placeholder={'email@email.com'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('email')}
              />
            )}
          />
        </div>
        <div className={'input-container flex flex-col gap-[3px] mt-[3px]'}>
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                type={'password'}
                label={'Password'}
                placeholder={'••••••••••••••••••••••••••••'}
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('password')}
                onFocus={() => clearErrors('password')}
              />
            )}
          />
        </div>
        <Button
          type="submit"
          className="md:mt-7 mt-6 w-full min-h-[50px] text-sm font-semibold shadow-[0px_2px_2px_0px_rgba(0,_0,_0,_0.1)]"
        >
          <div>Sign in</div>
        </Button>
      </form>
    </React.Fragment>
  );
};
